/**
 * What upgrading actually gives you, in the words the wizard now uses.
 *
 * Kept identical to the mobile copy at
 * modules/influencer/components/zap/zap-automations/ig-dm-automations/components/upgradeBenefits.ts.
 *
 * Every line is a real difference between the free/FOC entitlements and the
 * paid/trial ones, checked against plan_entitlements:
 *
 *   max_active_automation_posts   1     -> -1 (unlimited)
 *   allowed_content_types         post  -> post,story,live
 *   zap_link_limit                0     -> -1 (unlimited)
 *   zap_forms_enabled             false -> true
 *   follow_gate_enabled           false -> true
 *   any_media_automation_enabled  false -> true
 *   daily_interaction_limit       50    -> -1 (unlimited)
 *   auto_dm_branding_required     true  -> false
 *   storefront_enabled            false -> true
 *   link_in_bio_enabled           false -> true
 *
 * OLD copy, retired because it described the data model rather than the
 * outcome — "rules", "variations" and "product links (Zap) per rule" are
 * exactly the vocabulary the simplified wizard stopped using — and because its
 * last line promised something no entitlement backs:
 *     Unlimited active automations
 *     Unlimited product links (Zap) per rule
 *     Unlimited reply & DM variations
 *     More keywords per rule
 *     Advanced analytics & activity logs   <- no such entitlement exists
 */
export const UPGRADE_BENEFITS: string[] = [
  'Run as many Zaps as you like, all at once',
  'Zap your Stories and Lives, not just posts',
  'Earn affiliate revenue with product cards in DMs',
  'Collect leads with a form built right here',
  'Ask people to follow you before your DM arrives',
  'One Zap that covers every post, including new ones',
  'Your own storefront and link-in-bio page',
  'No daily cap on replies, and no ClanConnect footer',
];

/**
 * What to tell someone who cannot start another Zap.
 *
 * The limit counts POSTS, not Zaps: one Zap watching two posts uses two of the
 * allowance. So a creator with two Zaps can be told "3 of 1" and count only two
 * rows — which is what confused people in the first version.
 *
 * The explanation belongs in the ROWS ("Watching 2 posts"), not in a paragraph.
 * A second attempt spelled the whole rule out in prose and was too long to
 * read; these lines are deliberately one sentence each, with the arithmetic
 * left to the subtitle and the row labels.
 *
 * A Live Zap watches no posts but still takes a slot, so slots — not post
 * counts — are what the rows and the total are built from; see slotsUsedBy().
 *
 * OLD, both retired:
 *   `You have ${used} running and your plan covers ${cap}. Pause the ones you
 *    do not need right now, or upgrade to keep them all on.`   <- ambiguous
 *   `Every post a Zap watches counts as one. Your ${n} Zaps cover ${used} posts
 *    between them, and your plan covers ${allowance} at a time. Switching a Zap
 *    off frees up everything it watches — or upgrade to run as many as you
 *    like.`                                                     <- too long
 */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function planLimitMessage(used: number, limit: any, zapCount: number): string {
  const cap = typeof limit === 'number' ? limit : Number(limit);
  if (!Number.isFinite(cap) || cap < 0) return '';
  // One Zap on one post: there is no arithmetic to explain.
  if (zapCount === 1 && used === 1) return 'Switch it off below to free up your slot.';
  return 'Each post counts as one. Switch a Zap off to free up its posts.';
}

/** Sub-heading under the title — the whole arithmetic, in four words. */
export function planLimitSubtitle(used: number, limit: any): string {
  const cap = typeof limit === 'number' ? limit : Number(limit);
  if (!Number.isFinite(cap) || cap < 0) return '';
  return `${plural(used, 'post', 'posts')} automated · plan allows ${cap}`;
}

/**
 * What one Zap holds against the plan.
 *
 * A Live Zap has no posts attached but still occupies a slot, so it counts as
 * one — otherwise the rows would not add up to the total in the subtitle.
 */
export function slotsUsedBy(activePosts: number, contentType?: string): number {
  if (contentType === 'live') return 1;
  return activePosts;
}

/** Row label: what this Zap is actually holding. */
export function zapRowMeta(activePosts: number, contentType?: string): string {
  if (contentType === 'live') return 'Runs on your Lives';
  if (activePosts <= 0) return 'Running';
  // "story" pluralises irregularly, so both forms are spelled out.
  const [one, many] = contentType === 'story' ? ['story', 'stories'] : ['post', 'posts'];
  return `Watching ${plural(activePosts, one, many)}`;
}
